import { createActions } from 'redux-actions';
import { actionTypes } from './actionTypes';
import { produce } from 'immer';

import { COMPANY_HISTORY_CATEGORY_IDS } from '@model';

import { API } from '@service';

const INSIGHT_RESPONSE = {
  SUCCESS: 204,
};

const {
  analysis: {
    CHANGE_STOCKCHART_DATA,
    CHANGE_MARKETCAP_DATA,
    CHANGE_ANALYSIS_INSIGHT_DATA,
    CHANGE_TOTAL_REVENUE_DATA,
    CHANGE_DEAL_COMPLEXITY,
    SET_SHOW_KEY_CONTACTS,
    SAVE_KEY_CONTACTS_DATA,
    CHANGE_COMPANY_HISTORY_DATA,
    CHANGE_FORCEUPDATE_PROJECT_IDS,
    TOGGLE_LE_UPLOAD_MODAL,
    CHANGE_SHOULD_SHOW_OFFICE,
  },
  insights: { CHANGE_GD_PRINCIPLE_DATA },
} = actionTypes;

const analysisActions = createActions(
  {},
  CHANGE_STOCKCHART_DATA,
  CHANGE_MARKETCAP_DATA,
  CHANGE_ANALYSIS_INSIGHT_DATA,
  CHANGE_GD_PRINCIPLE_DATA,
  CHANGE_TOTAL_REVENUE_DATA,
  CHANGE_DEAL_COMPLEXITY,
  SET_SHOW_KEY_CONTACTS,
  SAVE_KEY_CONTACTS_DATA,
  CHANGE_COMPANY_HISTORY_DATA,
  CHANGE_FORCEUPDATE_PROJECT_IDS,
  TOGGLE_LE_UPLOAD_MODAL,
  CHANGE_SHOULD_SHOW_OFFICE,
);

const fetchStockData = (id) => async (dispatch) => {
  const data = await API.getStockDataRequest(id);

  dispatch(analysisActions.changeStockchartData({ id, data }));
};

// 抽帧数据，暂时用不到
function compressData(data) {
  const n = data.length;
  if (n <= 150) {
    return data; // 如果数据条数不超过140，不需要压缩
  }

  const k = Math.floor(n / 150); // 计算步长
  const compressedData = [];

  for (let i = 0; i < n; i += k) {
    compressedData.push(data[i]);
  }

  // 确保最终数据量不超过140条
  return compressedData.slice(0, 150);
}
const fetchMarketCapData = (id) => async (dispatch) => {
  const data = await API.getMarketCap(id);

  console.log('craft data: ', data);
  // data.buyer.marketCapList = compressData(data.buyer.marketCapList);
  // data.target.marketCapList = compressData(data.target.marketCapList);
  dispatch(analysisActions.changeMarketcapData({ id, data }));
};

const fetchTotalRevenue =
  (id, shouldUpdate = null) =>
  async (dispatch, getState) => {
    const hasData = Boolean(getState().analysis.totalRevenueData[id]);
    if (!hasData || shouldUpdate) {
      const data = await API.getTotalRevenue(id);
      dispatch(analysisActions.changeTotalRevenueData({ id, data }));
    }
  };

const fetchAnalysisInsightData =
  ({ projectId, segment, category, uuid }) =>
  async (dispatch) => {
    const data = await API.getAnalysisInsightRequest({
      projectId,
      segment,
      category,
      uuid,
    });

    dispatch(
      analysisActions.changeAnalysisInsightData({
        id: projectId,
        segment,
        category,
        data,
      }),
    );
  };

const fetchGdPrincipletData = (id) => async (dispatch) => {
  const data = await API.getGPrinciples(id);
  dispatch(
    analysisActions.changeGdPrincipleData({
      id,
      data,
    }),
  );
};

const handleDeleteInsight =
  ({ insightId, segment, category, uuid, field }) =>
  async (dispatch, getState) => {
    const projectId = getState().common.currentId;
    const result = await API.deleteInsightRequest(insightId, field);
    if (field === 'guidingPrinciple') {
      if (result) {
        await dispatch(fetchGdPrincipletData(projectId));
      }
      return { success: true };
    }

    if (result && result.status === INSIGHT_RESPONSE.SUCCESS) {
      await dispatch(
        fetchAnalysisInsightData({
          projectId,
          segment,
          category,
          uuid,
        }),
      );

      return { success: true };
    }
  };

const handleSaveInsight = (body) => async (dispatch) => {
  const { segment, category, uuid, projectId } = body;

  const urlSuffix = `/${segment}/${encodeURIComponent(
    category.replace(/\//g, '_').trim().toLowerCase(),
  )}`;
  const result = await API.editOrAddInsight({ ...body, urlSuffix });

  if (result?.id) {
    await dispatch(
      fetchAnalysisInsightData({
        projectId,
        segment,
        category,
        uuid,
      }),
    );
  }

  return { success: true };
};

const fetchCompanyHistoryData = (projectId) => async (dispatch, getState) => {
  const currentId = projectId || getState().common.currentId;

  const data = await API.getCompanyHistoryDataRequest(currentId);

  const savedData = {
    id: currentId,
    data: {
      data: enhanceData(data.history),
      hasData: Boolean(data?.project),
    },
  };

  dispatch(analysisActions.changeCompanyHistoryData(savedData));
};

const enhanceData = (data) => {
  if (!data) {
    return {};
  }

  return {
    buyer: formatHistoryData(data.buyer),
    target: formatHistoryData(data.target),
  };
};

const formatHistoryData = (data = []) => {
  if (!Array.isArray(data)) {
    return data;
  }

  return produce(data, (draft) => {
    const result = draft.reduce(
      (result, item) => {
        const { dataSource, years, categories, yearsWithCateId } = result;

        const currentCateId = COMPANY_HISTORY_CATEGORY_IDS[item.category];

        const temp = {
          ...item,
          year: item.year === '-1' ? ' ' : item.year,
          categoryLabel: item.category,
          category: currentCateId,
        };

        dataSource.push(temp);

        if (!years.includes(temp.year)) {
          years.push(temp.year);
        }

        if (!categories[temp.year]) {
          categories[temp.year] = [currentCateId];
        } else if (!categories[temp.year].includes(currentCateId)) {
          categories[temp.year].push(currentCateId);
        }
        if (!yearsWithCateId[temp.year]) {
          yearsWithCateId[temp.year] = [currentCateId];
        } else {
          yearsWithCateId[temp.year].push(currentCateId);
        }

        return {
          dataSource,
          years,
          categories,
          yearsWithCateId,
        };
      },
      {
        dataSource: [],
        years: [],
        categories: {},
        yearsWithCateId: {},
      },
    );

    return {
      ...result,
      years: result.years.sort((a, b) => Number(b) - Number(a)),
    };
  });
};

export default {
  ...analysisActions,
  fetchStockData,
  fetchMarketCapData,
  fetchTotalRevenue,
  fetchAnalysisInsightData,
  handleDeleteInsight,
  fetchGdPrincipletData,
  handleSaveInsight,
  fetchCompanyHistoryData,
};
